import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, Phone, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLOR = { Health: '#16A34A', Life: '#3B82F6', Business: '#F97316' };
const when = (d, t) => `${new Date(`${d}T12:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;

function BookingRow({ b }) {
  const cancelled = b.status === 'cancelled';
  return (
    <li className={`rounded-xl border border-[#E2E8F0] p-3 ${cancelled ? 'opacity-50 bg-[#F8FAFC]' : ''}`} data-testid={`week-booking-${b.booking_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: COLOR[b.kind] }}>{b.kind}</span>
        <span className={`font-semibold text-[#0A0F1C] ${cancelled ? 'line-through' : ''}`}>{b.name}</span>
        {cancelled && <span className="text-[10px] font-bold uppercase text-[#64748B]" data-testid={`week-booking-cancelled-${b.booking_id}`}>Cancelled</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">
        <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {when(b.date, b.time)}</span>
        <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:text-[#1D4ED8]"><Mail size={12} /> {b.email}</a>
        {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-[#1D4ED8]"><Phone size={12} /> {b.phone}</a>}
        {!cancelled && <Link to={`/admin/calendar?booking=${b.booking_id}&date=${b.date}`} className="font-semibold text-[#1D4ED8] hover:underline" data-testid={`week-booking-open-${b.booking_id}`}>Open booking →</Link>}
      </div>
    </li>
  );
}

function LaneChips({ rows, value, onChange }) {
  const live = rows.filter((b) => b.status !== 'cancelled');
  const opts = [['All', '#0A0F1C', live.length], ...Object.entries(COLOR).map(([k, c]) => [k, c, live.filter((b) => b.kind === k).length])];
  return (
    <div className="flex flex-wrap gap-2" data-testid="week-bookings-filters">
      {opts.map(([k, c, n]) => {
        const on = value === k;
        return (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`week-filter-${k.toLowerCase()}`} aria-pressed={on}
            className="rounded-full border-2 px-3 py-1 text-xs font-bold transition-colors"
            style={on ? { background: c, borderColor: c, color: '#fff' } : { borderColor: `${c}66`, color: c }}>
            {k} <span className="ml-0.5 opacity-80">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

export function WeekBookingsDialog({ week, label, onClose }) {
  const [rows, setRows] = useState(null);
  const [lane, setLane] = useState('All');
  useEffect(() => {
    if (!week) return;
    setRows(null);
    setLane('All');
    axios.get(`${API_URL}/api/admin/dashboard/jumpstart-weekly/${week}`, { withCredentials: true }).then((r) => setRows(r.data.bookings)).catch(() => setRows([]));
  }, [week]);
  const shown = (rows || []).filter((b) => lane === 'All' || b.kind === lane);
  return (
    <Dialog open={!!week} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="week-bookings-dialog">
        <DialogHeader><DialogTitle>Jumpstart bookings: {label}</DialogTitle><DialogDescription>Sessions booked during this week, by the time they were booked.</DialogDescription></DialogHeader>
        {!rows ? <p className="text-sm text-[#64748B]">Loading…</p>
          : rows.length === 0 ? <p className="text-sm text-[#64748B]" data-testid="week-bookings-empty">No Jumpstart sessions were booked this week.</p>
          : <>
            <LaneChips rows={rows} value={lane} onChange={setLane} />
            {shown.length === 0 ? <p className="text-sm text-[#64748B]" data-testid="week-bookings-filter-empty">No {lane} sessions were booked this week.</p>
              : <ul className="space-y-2" data-testid="week-bookings-list">{shown.map((b) => <BookingRow key={b.booking_id} b={b} />)}</ul>}
          </>}
      </DialogContent>
    </Dialog>
  );
}
